// MirrorTypesSection.jsx
// Mirrors division (08 00 01) - Mirror Types tab content
// Pattern: Follows SFTypesSection.jsx structure for consistent design language

import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { getWarrantyError, hasWarrantyErrors } from '../../utils/validateWarranty';
import { NumericInput } from '../../components/NumericInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { Plus, Trash2, Edit2, Upload, ClipboardPaste, Loader2, X, Image as ImageIcon, Info } from 'lucide-react';
import ColorPickerPopover from '../../components/ColorPickerPopover';

const DEFAULT_MARKER_COLOR = '#3B82F6';

export // Common mirror thicknesses for select dropdown
const COMMON_MIRROR_THICKNESSES = [
  { value: '1/8" (3mm)', label: '1/8" (3mm)' },
  { value: '3/16" (5mm)', label: '3/16" (5mm)' },
  { value: '1/4" (6mm)', label: '1/4" (6mm)' },
  { value: '3/8" (10mm)', label: '3/8" (10mm)' },
  { value: '1/2" (12mm)', label: '1/2" (12mm)' },
  { value: '5/8" (16mm)', label: '5/8" (16mm)' },
  { value: '3/4" (19mm)', label: '3/4" (19mm)' },
  { value: '1" (25mm)', label: '1" (25mm)' },
];

const MirrorTypesSection = ({
  // Data
  mirrorTypes = [],
  finishTypes = [],
  selectedMirrorTypeIds = [],
  newMirrorType,
  editingMirrorType,
  showMirrorTypeDialog,
  mirrorTypePictureUrl,
  pendingMirrorTypePicture,
  mirrorTypePictureUploading,
  setMirrorTypePictureUploading,
  operationLoading,
  // Permissions
  canEditCSIItems,
  canManageProject,
  // Handlers
  setNewMirrorType,
  setShowMirrorTypeDialog,
  setEditingMirrorType,
  setMirrorTypePictureUrl,
  setPendingMirrorTypePicture,
  setMirrorTypes,
  selectAllMirrorTypes,
  confirmBulkDelete,
  toggleMirrorTypeSelection,
  handleCreateMirrorType,
  handleDeleteMirrorType,
  handleDeleteMirrorTypePicture,
  openEditMirrorTypeDialog,
  resetMirrorTypeDialog,
  onViewDrawing,
  onPasteFromClipboard,
  // API (for color picker save)
  API,
  axios,
  toast,
}) => {
  // Reset form when dialog closes
  const handleDialogOpenChange = (open) => {
    if (open && !editingMirrorType) {
      setMirrorTypePictureUrl(null);
      if (pendingMirrorTypePicture?.previewUrl) {
        URL.revokeObjectURL(pendingMirrorTypePicture.previewUrl);
      }
      setPendingMirrorTypePicture(null);
      setNewMirrorType({
        name: '',
        manufacturer: '',
        model: '',
        mirrorThickness: '',
        frameMaterial: '',
        frameFinish: '',
        description: '',
        mirrorWarrantyYears: null,
        installationWarrantyYears: null,
        markerColor: DEFAULT_MARKER_COLOR
      });
    }
    setShowMirrorTypeDialog(open);
    if (!open) {
      resetMirrorTypeDialog();
    }
  };

  // Handle file input change for picture
  const handlePictureFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPendingMirrorTypePicture({ file, previewUrl });
      setMirrorTypePictureUrl(previewUrl);
    }
  };

  // Handle paste from clipboard for inline picture upload
  // Handle inline file upload
  const handleInlineFileUpload = async (e, mirrorTypeId) => {
    const file = e.target.files?.[0];
    if (!file || !setMirrorTypes || !API || !axios) return;
    
    if (mirrorTypePictureUploading) return; // prevent double upload
    setMirrorTypePictureUploading(mirrorTypeId);
    
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/mirror-types/${mirrorTypeId}/upload-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMirrorTypes(prev => prev.map(mt => 
        mt.id === mirrorTypeId ? { ...mt, pictureUrl: response.data.pictureUrl, pictureCloudinary: response.data.pictureCloudinary } : mt
      ));
      toast?.success('Picture uploaded successfully');
    } catch (err) {
      toast?.error('Failed to upload picture');
    } finally {
      setMirrorTypePictureUploading(null);
    }
  };

  // Remove pending picture
  const removePendingPicture = () => {
    if (pendingMirrorTypePicture?.previewUrl) {
      URL.revokeObjectURL(pendingMirrorTypePicture.previewUrl);
    }
    setPendingMirrorTypePicture(null);
    setMirrorTypePictureUrl(null);
  };

  return (
    <div>
      {/* Header with actions */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Mirror Types</h2>
        <div className="flex gap-2">
          {canEditCSIItems && mirrorTypes.length > 0 && (
            <>
              <Button 
                variant="outline"
                onClick={() => selectAllMirrorTypes(mirrorTypes)}
                data-testid="select-all-mirror-types-btn"
              >
                {selectedMirrorTypeIds.length === mirrorTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button 
                onClick={() => confirmBulkDelete('mirrorType')}
                disabled={selectedMirrorTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
                data-testid="delete-selected-mirror-types-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedMirrorTypeIds.length})
              </Button>
            </>
          )}
          {canEditCSIItems && (
            <Dialog open={showMirrorTypeDialog} onOpenChange={handleDialogOpenChange}>
              <DialogTrigger asChild>
                <Button data-testid="add-mirror-type-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Mirror Type
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <DialogTitle>{editingMirrorType ? 'Edit Mirror Type' : 'Create Mirror Type'}</DialogTitle>
                      <DialogDescription>Define a mirror type specification</DialogDescription>
                    </div>
                  </div>
                </DialogHeader>
                <form onSubmit={handleCreateMirrorType} className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type *</Label>
                      <Input 
                        value={newMirrorType.name} 
                        onChange={(e) => setNewMirrorType({...newMirrorType, name: e.target.value})} 
                        placeholder="e.g., Type A, Type B"
                        required 
                        data-testid="mirror-type-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Model *</Label>
                      <Input 
                        value={newMirrorType.model} 
                        onChange={(e) => setNewMirrorType({...newMirrorType, model: e.target.value})} 
                        placeholder="e.g., Standard, Premium"
                        required 
                        data-testid="mirror-type-model-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Manufacturer *</Label>
                      <Input 
                        value={newMirrorType.manufacturer} 
                        onChange={(e) => setNewMirrorType({...newMirrorType, manufacturer: e.target.value})} 
                        placeholder="e.g., Guardian, PPG"
                        required 
                        data-testid="mirror-type-manufacturer-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Mirror Thickness</Label>
                      <Select
                        value={newMirrorType.mirrorThickness || ''}
                        onValueChange={(val) => setNewMirrorType({...newMirrorType, mirrorThickness: val})}
                      >
                        <SelectTrigger data-testid="mirror-type-thickness-input">
                          <SelectValue placeholder="Select thickness" />
                        </SelectTrigger>
                        <SelectContent>
                          {COMMON_MIRROR_THICKNESSES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Frame Material</Label>
                      <Input 
                        value={newMirrorType.frameMaterial} 
                        onChange={(e) => setNewMirrorType({...newMirrorType, frameMaterial: e.target.value})} 
                        placeholder="e.g., Aluminum, Stainless Steel"
                        data-testid="mirror-type-frame-material-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Frame Finish</Label>
                      {finishTypes.length > 0 ? (
                        <Select
                          value={newMirrorType.frameFinish || ''}
                          onValueChange={(value) => setNewMirrorType({...newMirrorType, frameFinish: value})}
                        >
                          <SelectTrigger data-testid="mirror-type-frame-finish-select">
                            <SelectValue placeholder="Select finish" />
                          </SelectTrigger>
                          <SelectContent>
                            {finishTypes.filter(f => !f.isDeleted).map(finish => (
                              <SelectItem key={finish.id} value={finish.value}>
                                {finish.value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input 
                          value={newMirrorType.frameFinish || ''} 
                          onChange={(e) => setNewMirrorType({...newMirrorType, frameFinish: e.target.value})} 
                          placeholder="e.g., Brushed, Polished"
                          data-testid="mirror-type-frame-finish-input"
                        />
                      )}
                    </div>
                  </div>
                  
                  {/* Description */}
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea 
                      value={newMirrorType.description || ''} 
                      onChange={(e) => setNewMirrorType({...newMirrorType, description: e.target.value})} 
                      placeholder="Additional notes or specifications..."
                      rows={3}
                      data-testid="mirror-type-description-input"
                    />
                  </div>
                  
                  {/* Warranties */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm border-b pb-2">Warranties (Years)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Mirror Warranty</Label>
                        <NumericInput
                          value={newMirrorType.mirrorWarrantyYears || ''} 
                          onChange={(v) => setNewMirrorType({...newMirrorType, mirrorWarrantyYears: v ? parseInt(v) : null})} 
                          placeholder="e.g., 5"
                          className={getWarrantyError(newMirrorType.mirrorWarrantyYears) ? 'border-red-500' : ''}
                          data-testid="mirror-type-warranty-input"
                        />
                        {getWarrantyError(newMirrorType.mirrorWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newMirrorType.mirrorWarrantyYears)}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label>Installation Warranty</Label>
                        <NumericInput
                          value={newMirrorType.installationWarrantyYears || ''} 
                          onChange={(v) => setNewMirrorType({...newMirrorType, installationWarrantyYears: v ? parseInt(v) : null})} 
                          placeholder="e.g., 2"
                          className={getWarrantyError(newMirrorType.installationWarrantyYears) ? 'border-red-500' : ''}
                          data-testid="mirror-type-installation-warranty-input"
                        />
                        {getWarrantyError(newMirrorType.installationWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newMirrorType.installationWarrantyYears)}</p>}
                      </div>
                    </div>
                  </div>
                  
                  {/* Picture Upload */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm border-b pb-2">Mirror Type Picture</h4>
                    
                    {/* Show existing picture for edit mode */}
                    {editingMirrorType && (mirrorTypePictureUrl || editingMirrorType?.pictureCloudinary?.urls?.medium || editingMirrorType?.pictureUrl) && !pendingMirrorTypePicture && (
                      <div className="mb-4" key={mirrorTypePictureUrl || editingMirrorType?.pictureCloudinary?.urls?.medium}>
                        <img 
                          src={mirrorTypePictureUrl || editingMirrorType?.pictureCloudinary?.urls?.medium || editingMirrorType?.pictureUrl} 
                          alt="Current picture" 
                          className="max-w-full h-auto max-h-64 border rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const placeholder = e.target.nextSibling;
                            if (placeholder) placeholder.style.display = 'block';
                          }}
                        />
                        <div 
                          style={{ display: 'none' }} 
                          className="p-8 bg-slate-100 border-2 border-dashed rounded text-center text-slate-500"
                        >
                          <p>Image not found or failed to load</p>
                          <p className="text-sm mt-2">Please upload a new picture</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Show pending picture preview */}
                    {pendingMirrorTypePicture?.previewUrl && (
                      <div className="mb-4">
                        <div className="relative inline-block">
                          <img 
                            src={pendingMirrorTypePicture.previewUrl} 
                            alt="Preview" 
                            className="max-w-full h-auto max-h-64 border rounded"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={removePendingPicture}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        {!editingMirrorType && (
                          <p className="text-sm text-green-600 mt-2">Image ready - will be uploaded when mirror type is created</p>
                        )}
                      </div>
                    )}
                    
                    {/* Action buttons - always visible */}
                    <div className="space-y-2">
                      <Label>Upload Picture (PNG/JPEG/PDF)</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => onPasteFromClipboard && onPasteFromClipboard(editingMirrorType?.id || null)}
                          title="Paste image from clipboard"
                          disabled={mirrorTypePictureUploading === editingMirrorType?.id}
                        >
                          {mirrorTypePictureUploading === editingMirrorType?.id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <ClipboardPaste className="h-4 w-4 mr-2" />
                          )}
                          Paste
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById('mirror-type-picture-modal').click()}
                          disabled={mirrorTypePictureUploading === editingMirrorType?.id}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {(mirrorTypePictureUrl || editingMirrorType?.pictureCloudinary?.urls?.medium || editingMirrorType?.pictureUrl || pendingMirrorTypePicture) ? 'Change Picture' : 'Upload Picture'}
                        </Button>
                        {(mirrorTypePictureUrl || editingMirrorType?.pictureCloudinary?.urls?.medium || editingMirrorType?.pictureUrl) && editingMirrorType && handleDeleteMirrorTypePicture && (
                          <Button
                            type="button"
                            variant="destructive"
                            onClick={() => handleDeleteMirrorTypePicture(editingMirrorType.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Picture
                          </Button>
                        )}
                        <input
                          id="mirror-type-picture-modal"
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handlePictureFileChange}
                          style={{ display: 'none' }}
                        />
                      </div>
                      {!editingMirrorType && !pendingMirrorTypePicture && (
                        <p className="text-sm text-slate-500">Picture will be uploaded after creating the type</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Submit */}
                  <Button type="submit" className="w-full" disabled={operationLoading || hasWarrantyErrors([newMirrorType.mirrorWarrantyYears, newMirrorType.installationWarrantyYears])}>
                    {operationLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {editingMirrorType ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingMirrorType ? 'Update Mirror Type' : 'Create Mirror Type'
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
      
      {/* Mirror Types List - Card Based Layout (like Storefronts) */}
      {mirrorTypes.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <ImageIcon className="h-12 w-12 mx-auto mb-4 text-slate-400" />
            <p className="text-slate-600">No mirror types created yet</p>
            <p className="text-sm text-slate-400 mt-1">Click "Add Mirror Type" to create one</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {mirrorTypes.map((mt) => (
            <Card key={mt.id} className={selectedMirrorTypeIds.includes(mt.id) ? 'ring-2 ring-blue-500' : ''}>
              <div className="flex">
                {/* Checkbox column */}
                {canEditCSIItems && (
                  <div className="w-12 flex-shrink-0 flex items-start justify-center pt-4 border-r">
                    <Checkbox
                      checked={selectedMirrorTypeIds.includes(mt.id)}
                      onCheckedChange={() => toggleMirrorTypeSelection(mt.id)}
                      className="h-5 w-5"
                      data-testid={`mirror-type-checkbox-${mt.id}`}
                    />
                  </div>
                )}
                
                {/* Picture column - Left */}
                <div className="flex-shrink-0 p-4 border-r" style={{ width: '250px', height: '300px' }}>
                  {(mt.pictureCloudinary?.urls?.medium || mt.pictureUrl) ? (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-sm">Picture</h4>
                        <div className="flex gap-1">
                          {canEditCSIItems && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPasteFromClipboard && onPasteFromClipboard(mt.id)}
                                title="Paste image from clipboard"
                                disabled={mirrorTypePictureUploading === mt.id}
                              >
                                {mirrorTypePictureUploading === mt.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <ClipboardPaste className="h-3 w-3" />
                                )}
                              </Button>
                              <label className="cursor-pointer">
                                <Button variant="outline" size="sm" asChild>
                                  <span><Upload className="h-3 w-3" /></span>
                                </Button>
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="image/*,.pdf"
                                  onChange={(e) => handleInlineFileUpload(e, mt.id)}
                                />
                              </label>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteMirrorTypePicture && handleDeleteMirrorTypePicture(mt.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <div
                        className="flex-1 cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center overflow-hidden"
                        onClick={() => onViewDrawing && onViewDrawing({
                          url: mt.pictureCloudinary?.urls?.large || mt.pictureCloudinary?.urls?.original || mt.pictureUrl,
                          title: `${mt.name} - Picture`
                        })}
                      >
                        <img
                          src={mt.pictureCloudinary?.urls?.large || mt.pictureCloudinary?.urls?.medium || mt.pictureUrl}
                          alt="Mirror Type Picture"
                          className="max-w-full max-h-full object-contain border rounded"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-sm">Picture</h4>
                        {canEditCSIItems && (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onPasteFromClipboard && onPasteFromClipboard(mt.id)}
                              title="Paste image from clipboard"
                              disabled={mirrorTypePictureUploading === mt.id}
                            >
                              {mirrorTypePictureUploading === mt.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <ClipboardPaste className="h-3 w-3" />
                              )}
                            </Button>
                            <label className="cursor-pointer">
                              <Button variant="outline" size="sm" asChild>
                                <span><Upload className="h-3 w-3" /></span>
                              </Button>
                              <input
                                type="file"
                                className="hidden"
                                accept="image/*,.pdf"
                                onChange={(e) => handleInlineFileUpload(e, mt.id)}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 flex items-center justify-center">
                        {canEditCSIItems ? (
                          <label className="cursor-pointer w-full">
                            <div className="border-2 border-dashed rounded-lg p-4 text-center hover:bg-slate-50 transition-colors">
                              <Upload className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                              <p className="text-sm text-slate-600">Upload Picture</p>
                              <p className="text-xs text-slate-400 mt-1">PNG, JPG, PDF</p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*,.pdf"
                              onChange={(e) => handleInlineFileUpload(e, mt.id)}
                            />
                          </label>
                        ) : (
                          <div className="text-center text-slate-400">
                            <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                            <p className="text-sm">No Picture</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Content column - Right */}
                <div className="flex-1 p-4 flex flex-col justify-between" style={{ height: '300px' }}>
                  {/* Action buttons */}
                  {canEditCSIItems && (
                    <div className="flex gap-2 items-center justify-end mb-2">
                      {setMirrorTypes && API && axios && toast && (
                        <ColorPickerPopover
                          id={`mirror-${mt.id}`}
                          currentColor={mt.markerColor || DEFAULT_MARKER_COLOR}
                          pendingColor={mt.pendingMarkerColor}
                          isOpen={mt.colorPickerOpen}
                          isSaving={mt.savingColor}
                          onOpenChange={(open) => {
                            setMirrorTypes(prev => prev.map(t => 
                              t.id === mt.id ? { ...t, colorPickerOpen: open, pendingMarkerColor: open ? t.pendingMarkerColor : undefined } : t
                            ));
                          }}
                          onColorChange={(newColor) => {
                            setMirrorTypes(prev => prev.map(t => 
                              t.id === mt.id ? { ...t, pendingMarkerColor: newColor } : t
                            ));
                          }}
                          onSave={async () => {
                            setMirrorTypes(prev => prev.map(t => 
                              t.id === mt.id ? { ...t, savingColor: true } : t
                            ));
                            try {
                              await axios.put(`${API}/mirror-types/${mt.id}`, {
                                ...mt,
                                markerColor: mt.pendingMarkerColor
                              });
                              setMirrorTypes(prev => prev.map(t => 
                                t.id === mt.id ? { ...t, markerColor: mt.pendingMarkerColor, pendingMarkerColor: undefined, savingColor: false, colorPickerOpen: false } : t
                              ));
                              toast.success('Marker color saved');
                            } catch (error) {
                              setMirrorTypes(prev => prev.map(t => 
                                t.id === mt.id ? { ...t, savingColor: false } : t
                              ));
                              toast.error('Failed to update marker color');
                            }
                          }}
                        />
                      )}
                      <Button variant="outline" size="sm" onClick={() => openEditMirrorTypeDialog(mt)} data-testid={`edit-mirror-type-${mt.id}`}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteMirrorType(mt.id)} data-testid={`delete-mirror-type-${mt.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* 4 Rows of content */}
                  <div className="flex-1 flex flex-col justify-evenly text-sm">
                    {/* Row 1: Basic Info */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Type</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{mt.name}</div>
                      <div className="font-semibold w-[95px]">Manufacturer</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{mt.manufacturer}</div>
                      <div className="font-semibold w-[95px]">Model</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{mt.model}</div>
                    </div>
                    
                    {/* Row 2: Frame Info */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Thickness</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{mt.mirrorThickness || 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Frame Material</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{mt.frameMaterial || 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Frame Finish</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{mt.frameFinish || 'N/A'}</div>
                    </div>
                    
                    {/* Row 3: Warranties */}
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Mirror Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{mt.mirrorWarrantyYears ? `${mt.mirrorWarrantyYears} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Install Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{mt.installationWarrantyYears ? `${mt.installationWarrantyYears} Years` : 'N/A'}</div>
                    </div>
                    
                    {/* Row 4: Description */}
                    <div className="flex">
                      <div className="font-semibold w-[95px]">Description</div>
                      <div className="w-[10px]">:</div>
                      <div className="flex-1 truncate" title={mt.description || ''}>{mt.description || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MirrorTypesSection;
